import { DashboardTile } from "../components/DashboardTile";
import { TileHeader } from "../components/TileHeader";
import hypercardBgHorizontal from "../../../../../common/assets/images/background-glass.png";
import { useMemo } from "react";
import {
  useAccountQuery,
  useCardArtQuery,
  usePrimaryCardQuery,
} from "@common/utils/graphql";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, formatDollars } from "@common/components/utils";
import { Body } from "@hypercard/web-components";
import { twMerge } from "@/utils/utils";

type Props = {
  className?: string;
};

export const BalanceTile = ({ className }: Props) => {
  const {
    loading: accountLoading,
    data: accountData,
    error: accountError,
  } = useAccountQuery();
  const {
    loading: cardImgLoading,
    data: cardImgRaw,
    error: cardImgError,
  } = useCardArtQuery();
  const {
    loading: cardDigitsLoading,
    data: cardDigitsRaw,
    error: cardDigitsError,
  } = usePrimaryCardQuery();

  const hasErrored = accountError || cardImgError || cardDigitsError;

  const account = useMemo(() => {
    if (accountData?.account?.__typename !== "QueryAccountSuccess") {
      return undefined;
    }
    return accountData.account.data;
  }, [accountData]);

  const cardImg = useMemo(() => {
    if (cardImgRaw?.cardArt?.__typename !== "QueryCardArtSuccess") {
      return "";
    }
    return cardImgRaw.cardArt.data.frontImageUrl;
  }, [cardImgRaw]);

  const cardDigits = useMemo(() => {
    if (cardDigitsRaw?.primaryCard?.__typename !== "QueryPrimaryCardSuccess") {
      return "";
    }
    return cardDigitsRaw.primaryCard.data.last4Digits;
  }, [cardDigitsRaw]);

  const creditCardImage =
    cardImgLoading || cardDigitsLoading ? (
      <Skeleton className="h-[200px] w-[300px]" />
    ) : (
      <div className="relative">
        <img src={cardImg} width={300} />
        <div className="absolute bottom-5 left-4 text-xs text-white/70">
          **** {cardDigits}
        </div>
      </div>
    );

  const balanceText = formatDollars(account?.currentBalance);
  const paymentText = `Payment of ${formatDollars(account?.dueAmount)} due on ${formatDate(account?.dueDateUtc, "M/d")}`;

  return (
    <DashboardTile
      className={twMerge(
        "flex min-h-[280px] items-center space-x-6",
        className,
      )}
      style={{
        background: `url(${hypercardBgHorizontal}) 0 0 no-repeat`,
        backgroundPosition: "bottom right",
        backgroundSize: "cover",
      }}
    >
      {hasErrored ? (
        <Body size="sm" weight="medium" className="mx-auto my-auto text-center">
          Failed to load balance
        </Body>
      ) : (
        <>
          <div className="flex h-full flex-1 flex-col justify-between">
            <TileHeader>BALANCE</TileHeader>
            <div className="space-y-2">
              {accountLoading ? (
                <Skeleton className="h-14 w-[200px]" />
              ) : (
                <p className="font-serif text-[40px] font-medium">
                  {balanceText}
                </p>
              )}
              {accountLoading ? (
                <Skeleton className="h-4 w-[200px]" />
              ) : (
                <Body size="sm" weight="regular" className="text-white/70">
                  Credit Limit: {formatDollars(account?.creditLimit)}{" "}
                </Body>
              )}
            </div>
            {accountLoading ? (
              <Skeleton className="h-4 w-[200px]" />
            ) : (
              <Body size="sm" weight="regular" className="text-white/50">
                {paymentText}
              </Body>
            )}
          </div>
          <div className="hidden md:block">{creditCardImage}</div>
        </>
      )}
    </DashboardTile>
  );
};
